import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';

const SettingsModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 1200;
  display: ${props => props.$show ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const SettingsContainer = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const SettingsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 2px solid #eee;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px 12px 0 0;
`;

const SettingsTitle = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.8rem;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  opacity: 0.8;
  
  &:hover {
    opacity: 1;
    background: rgba(255, 255, 255, 0.1);
  }
`;

const SettingsContent = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const SettingSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SectionTitle = styled.h3`
  margin: 0;
  color: #333;
  font-size: 1.1rem;
  font-weight: 600;
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 0.5rem;
`;

const SettingRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  
  .setting-label {
    flex: 1;
    
    .title {
      font-weight: 500;
      color: #333;
      margin-bottom: 0.25rem;
    }
    
    .description {
      font-size: 0.875rem;
      color: #666;
      line-height: 1.3;
    }
  }
  
  .setting-control {
    margin-left: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const Toggle = styled.label`
  position: relative;
  display: inline-block;
  width: 54px;
  height: 28px;
  
  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: 0.3s;
    border-radius: 28px;
    
    &:before {
      position: absolute;
      content: "";
      height: 20px;
      width: 20px;
      left: 4px;
      bottom: 4px;
      background-color: white;
      transition: 0.3s;
      border-radius: 50%;
    }
  }
  
  input:checked + .slider {
    background-color: #007bff;
  }
  
  input:checked + .slider:before {
    transform: translateX(26px);
  }
`;

const Select = styled.select`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  min-width: 120px;
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const Input = styled.input`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  width: 80px;
  text-align: center;
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const DiscountGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

const DiscountInput = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  label {
    font-size: 0.8rem;
    color: #666;
    min-width: 40px;
  }
  
  input {
    flex: 1;
    padding: 0.4rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    text-align: center;
    font-size: 0.85rem;
    
    &:focus {
      outline: none;
      border-color: #007bff;
    }
  }
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid #eee;
  background: #f8f9fa;
  border-radius: 0 0 12px 12px;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;
  
  &.cancel {
    background: #6c757d;
    color: white;
    
    &:hover {
      background: #5a6268;
    }
  }
  
  &.save {
    background: #28a745;
    color: white;
    
    &:hover {
      background: #218838;
    }
  }
  
  &.reset {
    background: #ffc107;
    color: #212529;
    
    &:hover {
      background: #e0a800;
    }
  }
`;

const TpvSettings = ({ isOpen, onClose, settings, onSave }) => {
  const [localSettings, setLocalSettings] = useState({
    // Configuraciones de precios
    defaultShowPricesWithTax: true,
    autoSelectPriceDisplayByClientType: true,
    professionalClientsShowWithoutTax: true,
    
    // Configuraciones de descuentos
    enableQuickDiscounts: true,
    quickDiscountValues: [5, 10, 15], // Valores por defecto
    quickIncrementValues: [10], // Incrementos por defecto
    maxGlobalDiscountPercent: 50,
    maxGlobalDiscountAmount: 1000,
    
    // Configuraciones de comportamiento
    autoAddFirstCalculation: true,
    showItemDiscountInDescription: true,
    requireConfirmationForLargeDiscounts: true,
    largeDiscountThreshold: 25, // % a partir del cual pedir confirmación
    
    // Configuraciones de interfaz
    showTaxIncludedToggle: true,
    showGlobalDiscountSection: true,
    numpadDecimalPlaces: 2,
    currencySymbol: '€',
    
    // Configuraciones de caja
    defaultPaymentMethod: 'cash',
    suggestChangeAmounts: true,
    quickCashAmounts: [5, 10, 20, 50, 100],
    
    // Configuraciones de tickets
    showItemCodes: false,
    showDetailedTaxBreakdown: true,
    printTicketAutomatically: false
  });

  useEffect(() => {
    if (settings) {
      setLocalSettings({ ...localSettings, ...settings });
    }
  }, [settings, isOpen]);

  const handleSettingChange = (key, value) => {
    setLocalSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleQuickDiscountChange = (index, value) => {
    const newValues = [...localSettings.quickDiscountValues];
    newValues[index] = parseInt(value) || 0;
    handleSettingChange('quickDiscountValues', newValues);
  };

  const handleQuickCashAmountChange = (index, value) => {
    const newValues = [...localSettings.quickCashAmounts];
    newValues[index] = parseInt(value) || 0;
    handleSettingChange('quickCashAmounts', newValues);
  };

  const handleSave = () => {
    onSave(localSettings);
    toast.success('Configuración guardada correctamente');
    onClose();
  };

  const handleReset = () => {
    if (window.confirm('¿Estás seguro de que quieres restaurar la configuración por defecto?')) {
      setLocalSettings({
        defaultShowPricesWithTax: true,
        autoSelectPriceDisplayByClientType: true,
        professionalClientsShowWithoutTax: true,
        enableQuickDiscounts: true,
        quickDiscountValues: [5, 10, 15],
        quickIncrementValues: [10],
        maxGlobalDiscountPercent: 50,
        maxGlobalDiscountAmount: 1000,
        autoAddFirstCalculation: true,
        showItemDiscountInDescription: true,
        requireConfirmationForLargeDiscounts: true,
        largeDiscountThreshold: 25,
        showTaxIncludedToggle: true,
        showGlobalDiscountSection: true,
        numpadDecimalPlaces: 2,
        currencySymbol: '€',
        defaultPaymentMethod: 'cash',
        suggestChangeAmounts: true,
        quickCashAmounts: [5, 10, 20, 50, 100],
        showItemCodes: false,
        showDetailedTaxBreakdown: true,
        printTicketAutomatically: false
      });
      toast.info('Configuración restaurada a valores por defecto');
    }
  };

  return (
    <SettingsModal $show={isOpen}>
      <SettingsContainer>
        <SettingsHeader>
          <SettingsTitle>⚙️ Configuración TPV</SettingsTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </SettingsHeader>

        <SettingsContent>
          {/* Configuración de Precios */}
          <SettingSection>
            <SectionTitle>💰 Configuración de Precios</SectionTitle>
            
            <SettingRow>
              <div className="setting-label">
                <div className="title">Mostrar precios con IVA por defecto</div>
                <div className="description">Configuración inicial al abrir el TPV</div>
              </div>
              <div className="setting-control">
                <Toggle>
                  <input 
                    type="checkbox"
                    checked={localSettings.defaultShowPricesWithTax}
                    onChange={(e) => handleSettingChange('defaultShowPricesWithTax', e.target.checked)}
                  />
                  <span className="slider"></span>
                </Toggle>
              </div>
            </SettingRow>

            <SettingRow>
              <div className="setting-label">
                <div className="title">Auto-selección por tipo de cliente</div>
                <div className="description">Ajustar automáticamente los precios según el tipo de cliente</div>
              </div>
              <div className="setting-control">
                <Toggle>
                  <input 
                    type="checkbox"
                    checked={localSettings.autoSelectPriceDisplayByClientType}
                    onChange={(e) => handleSettingChange('autoSelectPriceDisplayByClientType', e.target.checked)}
                  />
                  <span className="slider"></span>
                </Toggle>
              </div>
            </SettingRow>

            <SettingRow>
              <div className="setting-label">
                <div className="title">Clientes profesionales sin IVA</div>
                <div className="description">Mostrar precios sin IVA a clientes profesionales automáticamente</div>
              </div>
              <div className="setting-control">
                <Toggle>
                  <input 
                    type="checkbox"
                    checked={localSettings.professionalClientsShowWithoutTax}
                    onChange={(e) => handleSettingChange('professionalClientsShowWithoutTax', e.target.checked)}
                  />
                  <span className="slider"></span>
                </Toggle>
              </div>
            </SettingRow>
          </SettingSection>

          {/* Configuración de Descuentos */}
          <SettingSection>
            <SectionTitle>🏷️ Configuración de Descuentos</SectionTitle>
            
            <SettingRow>
              <div className="setting-label">
                <div className="title">Habilitar descuentos rápidos</div>
                <div className="description">Mostrar botones de descuento/incremento rápido en cada artículo</div>
              </div>
              <div className="setting-control">
                <Toggle>
                  <input 
                    type="checkbox"
                    checked={localSettings.enableQuickDiscounts}
                    onChange={(e) => handleSettingChange('enableQuickDiscounts', e.target.checked)}
                  />
                  <span className="slider"></span>
                </Toggle>
              </div>
            </SettingRow>

            {localSettings.enableQuickDiscounts && (
              <SettingRow>
                <div className="setting-label">
                  <div className="title">Valores de descuento rápido (%)</div>
                  <div className="description">Configura los porcentajes de descuento en los botones rápidos</div>
                  <DiscountGrid>
                    {localSettings.quickDiscountValues.map((value, index) => (
                      <DiscountInput key={index}>
                        <label>-{index + 1}:</label>
                        <input
                          type="number"
                          value={value}
                          onChange={(e) => handleQuickDiscountChange(index, e.target.value)}
                          min="0"
                          max="100"
                        />
                        <span>%</span>
                      </DiscountInput>
                    ))}
                  </DiscountGrid>
                </div>
              </SettingRow>
            )}

            <SettingRow>
              <div className="setting-label">
                <div className="title">Límite de descuento global</div>
                <div className="description">Máximo descuento permitido</div>
              </div>
              <div className="setting-control">
                <Input
                  type="number"
                  value={localSettings.maxGlobalDiscountPercent}
                  onChange={(e) => handleSettingChange('maxGlobalDiscountPercent', parseInt(e.target.value) || 0)}
                  min="0"
                  max="100"
                />
                <span>%</span>
              </div>
            </SettingRow>
          </SettingSection>

          {/* Configuración de Comportamiento */}
          <SettingSection>
            <SectionTitle>🔧 Comportamiento</SectionTitle>
            
            <SettingRow>
              <div className="setting-label">
                <div className="title">Auto-añadir primer cálculo</div>
                <div className="description">Añadir automáticamente el primer artículo calculado al ticket</div>
              </div>
              <div className="setting-control">
                <Toggle>
                  <input 
                    type="checkbox"
                    checked={localSettings.autoAddFirstCalculation}
                    onChange={(e) => handleSettingChange('autoAddFirstCalculation', e.target.checked)}
                  />
                  <span className="slider"></span>
                </Toggle>
              </div>
            </SettingRow>

            <SettingRow>
              <div className="setting-label">
                <div className="title">Confirmar descuentos grandes</div>
                <div className="description">Pedir confirmación para descuentos superiores al umbral</div>
              </div>
              <div className="setting-control">
                <Input
                  type="number"
                  value={localSettings.largeDiscountThreshold}
                  onChange={(e) => handleSettingChange('largeDiscountThreshold', parseInt(e.target.value) || 0)}
                  min="0"
                  max="100"
                />
                <span>%</span>
              </div>
            </SettingRow>
          </SettingSection>

          {/* Configuración de Caja */}
          <SettingSection>
            <SectionTitle>💵 Configuración de Caja</SectionTitle>
            
            <SettingRow>
              <div className="setting-label">
                <div className="title">Método de pago por defecto</div>
                <div className="description">Método seleccionado automáticamente</div>
              </div>
              <div className="setting-control">
                <Select
                  value={localSettings.defaultPaymentMethod}
                  onChange={(e) => handleSettingChange('defaultPaymentMethod', e.target.value)}
                >
                  <option value="cash">Efectivo</option>
                  <option value="card">Tarjeta</option>
                  <option value="transfer">Transferencia</option>
                  <option value="mixed">Mixto</option>
                </Select>
              </div>
            </SettingRow>

            <SettingRow>
              <div className="setting-label">
                <div className="title">Importes rápidos de efectivo (€)</div>
                <div className="description">Botones de cantidades comunes para pagos en efectivo</div>
                <DiscountGrid>
                  {localSettings.quickCashAmounts.map((value, index) => (
                    <DiscountInput key={index}>
                      <label>{index + 1}:</label>
                      <input
                        type="number"
                        value={value}
                        onChange={(e) => handleQuickCashAmountChange(index, e.target.value)}
                        min="0"
                        max="1000"
                      />
                      <span>€</span>
                    </DiscountInput>
                  ))}
                </DiscountGrid>
              </div>
            </SettingRow>
          </SettingSection>
        </SettingsContent>

        <ActionButtons>
          <Button className="reset" onClick={handleReset}>
            🔄 Restaurar
          </Button>
          <Button className="cancel" onClick={onClose}>
            Cancelar
          </Button>
          <Button className="save" onClick={handleSave}>
            💾 Guardar
          </Button>
        </ActionButtons>
      </SettingsContainer>
    </SettingsModal>
  );
};

export default TpvSettings;